import React, { createContext, useCallback, useContext, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { DonateForm } from './DonateForm';
import { DonateSuccess } from './DonateSuccess';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DonateContext = createContext({ openDonate: () => {} });
export const useDonate = () => useContext(DonateContext);

const loadCheckout = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export const DonateProvider = ({ children }) => {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [donation, setDonation] = useState(null);

  const openDonate = useCallback(() => { setDonation(null); setError(''); setOpen(true); }, []);

  const startPayment = async ({ amount, name, phone, show_on_wall, place }) => {
    setBusy(true); setError('');
    try {
      const ok = await loadCheckout();
      if (!ok) throw new Error(t('donation.failed'));
      const { data: order } = await axios.post(`${API}/donations/order`, { amount, name, phone, show_on_wall, place, lang });
      setOpen(false);
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
        name: 'Jai Bheem Seva Sangha', description: t('donation.title'),
        prefill: { name, contact: phone }, theme: { color: '#0b2a6b' },
        handler: async (res) => {
          try {
            const { data } = await axios.post(`${API}/donations/verify`, res);
            setDonation(data);
          } catch (e) { setError(e?.response?.data?.detail || t('donation.failed')); }
          setBusy(false); setOpen(true);
        },
        modal: { ondismiss: () => { setBusy(false); setError(t('donation.cancelled')); setOpen(true); } },
      });
      rzp.on('payment.failed', () => setError(t('donation.failed')));
      rzp.open();
    } catch (e) {
      setBusy(false);
      setError(e?.response?.data?.detail || e.message || t('donation.failed'));
    }
  };

  return (
    <DonateContext.Provider value={{ openDonate }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="jbss-donate-dialog" data-testid="donate-dialog">
          <DialogHeader>
            <DialogTitle className="jbss-donate-title">{donation ? t('donation.successTitle') : t('donation.title')}</DialogTitle>
            <DialogDescription className="jbss-donate-sub">{donation ? t('donation.successMsg') : t('donation.subtitle')}</DialogDescription>
          </DialogHeader>
          {donation
            ? <DonateSuccess donation={donation} onClose={() => setOpen(false)} />
            : <DonateForm busy={busy} error={error} onSubmit={startPayment} />}
        </DialogContent>
      </Dialog>
    </DonateContext.Provider>
  );
};
